import { Link, useSearchParams } from "react-router-dom";
import { useParcoursList } from "../../hooks/useParcoursList";
import { useLieuxList } from "../../hooks/useLieuxList";
import ParcoursCard from "./ParcoursCard";
import { filterUpcoming } from "../parcoursVisibility";

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") ?? "";

  const parcours = useParcoursList({ search: q, page: 0, size: 12 });
  const lieux = useLieuxList({ search: q, page: 0, size: 12 });

  // Artists are stored as objects on each parcours — match by substring against their names.
  const lower = q.trim().toLowerCase();
  const matchingArtistNames =
    lower.length > 0 && parcours.data
      ? Array.from(
          new Set(
            parcours.data.content.flatMap((p) =>
              (p.artists ?? [])
                .map((a) => a.name)
                .filter((n) => n.toLowerCase().includes(lower)),
            ),
          ),
        )
      : [];

  return (
    <section className="fo-section">
      <div className="container-w" style={{ padding: "0 40px" }}>
        <h1 className="fo-section__title">Recherche</h1>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            const value = new FormData(e.currentTarget).get("q")?.toString() ?? "";
            setParams(value ? { q: value } : {});
          }}
          className="fo-search"
        >
          <input
            type="search"
            name="q"
            defaultValue={q}
            placeholder="Parcours, artiste, lieu…"
            autoFocus
          />
        </form>

        {q.trim().length === 0 ? (
          <div className="fo-empty">Saisissez un terme pour rechercher.</div>
        ) : (
          <>
            {(() => {
              const upcoming = parcours.data ? filterUpcoming(parcours.data.content) : [];
              return (
                <SearchSection title="Parcours" total={upcoming.length} loading={parcours.isLoading}>
                  {upcoming.length > 0 && (
                    <div className="fo-grid">
                      {upcoming.map((p) => (
                        <ParcoursCard key={p.id} parcours={p} />
                      ))}
                    </div>
                  )}
                </SearchSection>
              );
            })()}

            <SearchSection
              title="Artistes"
              total={matchingArtistNames.length}
              loading={parcours.isLoading}
            >
              {matchingArtistNames.length > 0 && (
                <ul className="fo-list">
                  {matchingArtistNames.map((name) => (
                    <li key={name} className="fo-list__item">
                      <h3 className="fo-list__item-title">{name}</h3>
                    </li>
                  ))}
                </ul>
              )}
            </SearchSection>

            <SearchSection title="Lieux" total={lieux.data?.totalElements} loading={lieux.isLoading}>
              {lieux.data && lieux.data.content.length > 0 && (
                <ul className="fo-list">
                  {lieux.data.content.map((l) => (
                    <li key={l.id} className="fo-list__item">
                      <h3 className="fo-list__item-title">{l.name}</h3>
                      {l.address?.city && (
                        <div className="fo-list__item-meta">{l.address.city}</div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </SearchSection>

            <p style={{ marginTop: "var(--sp-6)", fontSize: 13, color: "var(--ink-mute)" }}>
              <Link to="/parcours">← Tous les parcours</Link>
            </p>
          </>
        )}
      </div>
    </section>
  );
}

function SearchSection({
  title,
  total,
  loading,
  children,
}: {
  title: string;
  total?: number;
  loading?: boolean;
  children: React.ReactNode;
}) {
  return (
    <section style={{ marginTop: "var(--sp-7)" }}>
      <h2 style={{ fontFamily: "var(--font-display)", fontSize: 28, textTransform: "uppercase", margin: 0 }}>
        {title}{" "}
        {!loading && (
          <span style={{ color: "var(--ink-mute)", fontSize: 14, fontFamily: "var(--font-body)" }}>
            ({total ?? 0})
          </span>
        )}
      </h2>
      <div style={{ marginTop: "var(--sp-4)" }}>
        {loading ? (
          <div className="fo-loading">Recherche en cours…</div>
        ) : !total ? (
          <div className="fo-empty">Aucun résultat.</div>
        ) : (
          children
        )}
      </div>
    </section>
  );
}
